// @ts-nocheck
/**
 * Career Position Search Component
 * 
 * Handles the search filter interface for career opportunities.
 * Provides Province/State and City dropdown filtering with client-side data.
 * 
 * @typedef {Object} CareerPositionSearchRefs
 * @property {HTMLFormElement|null} searchForm - The search form element
 * @property {HTMLSelectElement|null} provinceSelect - Province/State dropdown
 * @property {HTMLSelectElement|null} citySelect - City dropdown
 * @property {HTMLButtonElement|null} searchButton - Search button
 * @property {HTMLButtonElement|null} clearButton - Clear filters button
 * 
 * @extends {HTMLElement}
 */
class CareerPositionSearch extends HTMLElement {
  requiredRefs = ['searchForm', 'provinceSelect', 'citySelect', 'searchButton', 'clearButton'];

  /**
   * Get refs object for accessing DOM elements
   * @returns {CareerPositionSearchRefs}
   */
  get refs() {
    return {
      searchForm: this.querySelector('[ref="searchForm"]'),
      provinceSelect: this.querySelector('[ref="provinceSelect"]'),
      citySelect: this.querySelector('[ref="citySelect"]'),
      searchButton: this.querySelector('[ref="searchButton"]'),
      clearButton: this.querySelector('[ref="clearButton"]')
    };
  }

  /** @type {Object|null} */
  #careersData = null;

  /** @type {Object} */
  #filterParams = { province: '', city: '' };

  connectedCallback() {
    // Initialize configuration from data attributes
    this.shopDomain = this.dataset.shopDomain;
    this.apiVersion = this.dataset.storefrontApiVersion || '2025-10';
    this.accessToken = this.dataset.storefrontAccessToken;

    this.#initialize();
  }

  /**
   * Initialize the component
   */
  async #initialize() {
    // Validate required configuration
    if (!this.accessToken || !this.shopDomain) {
      console.error('CareerPositionSearch: Missing required configuration (accessToken or shopDomain)');
      return;
    }

    await this.#fetchCareersData();
    this.#restoreFiltersFromURL();
    this.#addEventListeners();
    this.handleSearch(new Event('submit'));
  }

  /**
   * Fetch careers data from Shopify Storefront API
   */
  async #fetchCareersData() {
    try {
      // Query both metaobjects in parallel
      const [locationsData, positionsData] = await Promise.all([
        this.#fetchMetaobjects('careers_location'),
        this.#fetchMetaobjects('career_positions')
      ]);

      // Transform data to Record format
      const locations = this.#transformLocationsData(locationsData);
      const positions = this.#transformPositionsData(positionsData);

      // Merge positions into locations
      this.#careersData = this.#mergePositionsToLocations(locations, positions);

      // Populate provinces and dispatch initial data
      this.#populateProvinces();
      this.#dispatchDataUpdate();
    } catch (error) {
      console.error('Error fetching careers data:', error);
    }
  }

  /**
   * Fetch all metaobjects from Shopify Storefront API using pagination
   * @param {string} type - Metaobject type
   * @param {number} pageSize - Number of items per page (default: 100)
   * @returns {Promise<Array>} All edges from all pages
   */
  async #fetchMetaobjects(type, pageSize = 100) {
    const query = `
    query getMetaobjects($type: String!, $first: Int!, $after: String) {
      metaobjects(type: $type, first: $first, after: $after) {
        edges {
          node {
            id
            handle
            updatedAt
            type
            fields {
              key
              value
              reference {
                ... on Metaobject {
                  id
                  handle
                }
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

    const allEdges = [];
    let hasNextPage = true;
    let cursor = null;

    while (hasNextPage) {
      const response = await fetch(`https://${this.shopDomain}/api/${this.apiVersion}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.accessToken
        },
        body: JSON.stringify({
          query,
          variables: {
            type,
            first: pageSize,
            after: cursor
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const metaobjects = data.data?.metaobjects;

      if (!metaobjects) {
        throw new Error('Invalid response format from GraphQL API');
      }

      // Accumulate edges from this page
      if (metaobjects.edges && metaobjects.edges.length > 0) {
        allEdges.push(...metaobjects.edges);
      }

      // Check if there are more pages
      const pageInfo = metaobjects.pageInfo;
      hasNextPage = pageInfo?.hasNextPage || false;
      cursor = pageInfo?.endCursor || null;
    }

    return allEdges;
  }

  /**
   * Transform locations data from API to Record format
   * @param {Array} locationsData - Raw locations data from API
   * @returns {Array}
   */
  #transformLocationsData(locationsData) {
    return locationsData.map(edge => {
      const node = edge.node;
      const fields = {};

      node.fields.forEach(field => {
        if (field.value) {
          fields[field.key] = field.value;
        }
      });

      return {
        id: node.id,
        handle: node.handle,
        location_name: fields.location_name || '',
        city: fields.city || '',
        province_state: fields.province_state || '',
        is_hiring: fields.is_hiring === 'true',
        priority: parseInt(fields.priority) || 0,
        email: fields.franchisee_email || ''
      };
    });
  }

  /**
   * Transform positions data from API to Record format
   * @param {Array} positionsData - Raw positions data from API
   * @returns {Array}
   */
  #transformPositionsData(positionsData) {
    return positionsData.map(edge => {
      const node = edge.node;
      const fields = {};

      node.fields.forEach(field => {
        if (field.value) {
          fields[field.key] = field.value;
        } else if (field.reference) {
          fields[field.key] = field.reference;
        }
      });

      return {
        id: node.id,
        handle: node.handle,
        updatedAt: node.updatedAt,
        position_name: fields.position_name || '',
        careers_location: fields.careers_location
      };
    });
  }

  /**
   * Merge positions into locations
   * @param {Array} locations - Locations data
   * @param {Array} positions - Positions data
   * @returns {Object}
   */
  #mergePositionsToLocations(locations, positions) {
    // Get sort mode from section container
    const sectionContainer = this.closest('[data-positions-sort]');
    const sortMode = sectionContainer?.getAttribute('data-positions-sort');
    const locationsWithPositions = locations.map(location => {

      // Prepare positions for this location
      let locationPositions = positions
        .filter(position => position.careers_location === location.id)
        .map(position => ({
          name: position.position_name,
          updatedAt: position.updatedAt
        }));

      // Sort by selected mode
      if (sortMode === 'updated') {
        locationPositions.sort((a, b) => {
          const at = a.updatedAt ? Date.parse(a.updatedAt) : 0;
          const bt = b.updatedAt ? Date.parse(b.updatedAt) : 0;
          return bt - at; // newest first
        });
      } else if (sortMode === 'name') {
        locationPositions.sort((a, b) => a.name.localeCompare(b.name));
      }

      // Map back to string list
      locationPositions = locationPositions.map(p => p.name);

      return {
        ...location,
        positions: locationPositions.join(',')
      };
    }).filter(location => location.positions.length > 0)
      .sort((a, b) => a.priority - b.priority);
    return {
      locations: locationsWithPositions
    };
  }

  /**
   * Dispatch data update event with filtered careers locations
   */
  #dispatchDataUpdate() {
    if (!this.#careersData) return;

    // Filter locations based on current filters
    let filteredLocations = this.#careersData.locations.filter(location => {
      if (!location.is_hiring) return false;

      if (this.#filterParams.province && location.province_state !== this.#filterParams.province) {
        return false;
      }

      if (this.#filterParams.city && location.city !== this.#filterParams.city) {
        return false;
      }

      return true;
    });

    // Sort by priority
    filteredLocations.sort((a, b) => (a.priority || 0) - (b.priority || 0));

    document.dispatchEvent(new CustomEvent('data:update', {
      detail: {
        locations: filteredLocations,
        filters: this.#filterParams
      },
      bubbles: true
    }));
  }

  /**
   * Populate province dropdown from loaded data
   */
  #populateProvinces() {
    const { provinceSelect } = this.refs;
    if (!provinceSelect || !this.#careersData) return;

    // Get unique provinces from locations
    const provinces = [...new Set(
      this.#careersData?.locations
        .filter(location => location.is_hiring)
        .map(location => location.province_state)
    )].sort();

    // Clear existing options except the first one

    for (const province of provinces) {
      const option = document.createElement('option');
      option.value = province;
      option.textContent = province;
      provinceSelect.appendChild(option);
    }
  }

  /**
   * Handle province change event
   * @param {Event} event - Change event
   */
  handleProvinceChange(event) {
    const target = event.target;
    if (!target || !(target instanceof HTMLSelectElement)) return;
    const province = target.value;
    target.setAttribute('value', province);

    if (province) {
      this.#populateCities(province);
    } else {
      this.#resetCityDropdown();
    }
  }

  /**
   * Populate city dropdown for selected province
   * @param {string} province - Selected province/state
   */
  #populateCities(province) {
    const { citySelect } = this.refs;
    if (!citySelect || !this.#careersData) return;

    // Get unique cities for the selected province
    const cities = [...new Set(
      this.#careersData.locations
        .filter(location =>
          location.is_hiring &&
          location.province_state === province
        )
        .map(location => location.city)
    )].sort();

    // When province is selected, show "--Select City--" format
    const baseSelectCityText = this.getAttribute('data-select-city-text') || 'Select City';
    const selectCityText = `-- ${baseSelectCityText} --`;
    citySelect.innerHTML = `<option value="">${selectCityText}</option>`;

    for (const city of cities) {
      const option = document.createElement('option');
      option.value = city;
      option.textContent = city;
      citySelect.appendChild(option);
    }

    // Enable city dropdown
    citySelect.disabled = false;
    citySelect.setAttribute('value', '');
  }

  /**
   * Setup floating label behavior for form fields
   */
  #addEventListeners() {
    const { provinceSelect, citySelect, searchButton, clearButton } = this.refs;

    if (provinceSelect) {
      provinceSelect.addEventListener('change', (e) => {
        this.#updateFloatingLabel(provinceSelect);
        this.handleProvinceChange(e);
      });
      this.#updateFloatingLabel(provinceSelect);
    }

    if (citySelect) {
      citySelect.addEventListener('change', () => {
        this.#updateFloatingLabel(citySelect);
      });
      this.#updateFloatingLabel(citySelect);
    }

    if (searchButton) {
      searchButton.addEventListener('click', (e) => {
        this.handleSearch(e);
      });
    }

    if (clearButton) {
      clearButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleClear(e);
      });
    }
  }

  /**
   * Update floating label state for select elements
   * @param {HTMLSelectElement} select - Select element
   */
  #updateFloatingLabel(select) {
    const label = select.nextElementSibling;
    if (label && label.tagName === 'LABEL') {
      if (select.value && select.value !== '') {
        label.classList.add('form-field__label--floating');
      } else {
        label.classList.remove('form-field__label--floating');
      }
    }
  }


  /**
   * Reset city dropdown to initial state
   */
  #resetCityDropdown() {
    const { citySelect } = this.refs;
    if (!citySelect) return;

    citySelect.disabled = true;
    // When no province is selected, show base text without dashes
    const selectCityText = this.getAttribute('data-select-city-text') || 'Select City';
    citySelect.innerHTML = `<option value="" disabled selected>${selectCityText}</option>`;
    citySelect.value = '';
    citySelect.setAttribute('value', '');
  }

  /**
   * Handle search form submission
   * @param {Event} event - Submit event
   */
  handleSearch(event) {
    event.preventDefault();

    const { provinceSelect, citySelect } = this.refs;
    if (!provinceSelect || !citySelect) return;

    this.#filterParams = {
      province: provinceSelect.value,
      city: citySelect.value
    };

    // Update URL
    this.#updateURL(this.#filterParams);

    // Dispatch data update event
    this.#dispatchDataUpdate();
  }

  /**
   * Handle clear button click
   */
  handleClear() {
    const { provinceSelect } = this.refs;
    if (!provinceSelect) return;

    provinceSelect.value = '';
    this.#resetCityDropdown();

    // Clear filter params
    this.#filterParams = { province: '', city: '' };

    // Clear URL parameters
    const url = new URL(window.location.href);
    url.searchParams.delete('province');
    url.searchParams.delete('city');
    window.history.pushState({}, '', url.toString());

    // Dispatch data update event
    this.#dispatchDataUpdate();
  }

  /**
   * Update URL with filter parameters
   * @param {Object} filters - Filter values
   * @param {string} filters.province - Selected province
   * @param {string} filters.city - Selected city
   */
  #updateURL(filters) {
    const url = new URL(window.location.href);

    for (const [key, value] of Object.entries(filters)) {
      if (value) {
        url.searchParams.set(key, value);
      } else {
        url.searchParams.delete(key);
      }
    }

    window.history.pushState({}, '', url.toString());
  }

  /**
   * Restore filters from URL parameters
   */
  #restoreFiltersFromURL() {
    const url = new URL(window.location.href);
    const province = url.searchParams.get('province');
    const city = url.searchParams.get('city');

    if (province) {
      const { provinceSelect, citySelect } = this.refs;
      if (!provinceSelect || !citySelect) return;

      provinceSelect.value = province;
      this.#filterParams.province = province;

      if (city) {
        this.#populateCities(province);
        setTimeout(() => {
          citySelect.value = city;
          this.#filterParams.city = city;
        }, 100);
      } else {
        this.#populateCities(province);
      }
    }
  }
}

if (!customElements.get('career-position-search')) {
  customElements.define('career-position-search', CareerPositionSearch);
}

